import React from "react";
import { Group, Text, Tooltip } from "@mantine/core";
import { IconArrowUpRight, IconArrowDownRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { textWithLineBreaks } from "../common/TextWithLineBreaks";

interface PercentageIndicatorProps {
  count: number;
  previousCount: number;
  percentageChange: number;
  useColorToDisplayCount?: boolean;
}

/**
 * A reusable component that displays a value with a color-coded percentage change indicator
 */
const PercentageIndicator: React.FC<PercentageIndicatorProps> = ({
  count,
  percentageChange,
  previousCount,
  useColorToDisplayCount = false,
}) => {
  const { t } = useTranslation();
  const isPositive = percentageChange > 0 && previousCount > 0;
  const isNeutral = percentageChange === 0 || previousCount === 0;
  const color = isPositive ? "green" : isNeutral ? "gray" : "orange";
  const Icon = isPositive ? IconArrowUpRight : isNeutral ? null : IconArrowDownRight;

  const textColor = useColorToDisplayCount
    ? count === 0
      ? "red"
      : count > 0 && count <= 10
        ? "orange"
        : "inherit"
    : "inherit";

  const tooltipLabel = t("analytics.common.percentageIndicator.tooltip", {
    previousCount,
    percentageChange,
  });

  return (
    <Group align="center" gap="xs" w="auto" display="inline-flex">
      <Tooltip label={textWithLineBreaks(tooltipLabel)} withArrow>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <Text style={{ color: textColor }}>{count}</Text>
          {Icon && <Icon color={color} size={16} />}
        </div>
      </Tooltip>
    </Group>
  );
};

export default PercentageIndicator;
